import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { getSupabase, supabaseConfigured } from './supabase'

/**
 * Who is signed in.
 *
 * ── THREE STATES, NOT TWO ──────────────────────────────────────────────────
 * "Loading", "signed in" and "signed out" are different things. Treating the
 * first render as signed-out is what makes an app flash a sign-in screen at
 * somebody who is already signed in, and — worse here — would let a page
 * conclude a customer has no domains when it simply has not looked yet.
 *
 * ── THE SESSION IS WATCHED, NOT SAMPLED ────────────────────────────────────
 * Supabase refreshes the access token in the background and can sign a user
 * out from another tab. Subscribing means the token handed to the Worker is
 * always the live one; reading it once at mount would hand out a token that
 * expires mid-purchase.
 */

interface AuthState {
  session: Session | null
  user: User | null
  /** True until the first session check has completed. */
  loading: boolean
  configured: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState>({
  session: null,
  user: null,
  loading: true,
  configured: false,
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const supabase = getSupabase()
    if (!supabase) {
      // Nothing to wait for: either this is the static build or the project
      // is not configured. Either way the answer is "not signed in", known.
      setLoading(false)
      return
    }

    let active = true

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setLoading(false)
    })

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return
      setSession(next)
      setLoading(false)
    })

    return () => {
      active = false
      subscription.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      configured: supabaseConfigured,
      signOut: async () => {
        await getSupabase()?.auth.signOut()
      },
    }),
    [session, loading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  return useContext(AuthContext)
}

/**
 * The access token to send to the Worker, refreshed if it is about to expire.
 *
 * Read at the moment of the call rather than held in a variable: a purchase
 * can sit on the confirmation screen for several minutes, which is long
 * enough for a token captured at mount to be stale by the time it is used.
 */
export async function getAccessToken(): Promise<string | null> {
  const supabase = getSupabase()
  if (!supabase) return null
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}
